import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { format } from 'date-fns';
import { CalendarIcon, Download, Eye, Search, Filter, History, X, Pencil, Trash2, ShieldAlert } from 'lucide-react';
import { downloadFile } from '../../utils/download';
import { toast } from 'sonner';
import axios from 'axios';

export function DispatchHistory({
  dispatchHistory, historyLoading, historyTotal, historyPage, historyTotalPages,
  setHistoryPage, fetchDispatchHistory, selectedPOData, canEditDelete,
  buyers, openEditModal, openDeleteModal
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [filterPO, setFilterPO] = useState('');
  const [filterBuyer, setFilterBuyer] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewPdfUrl, setViewPdfUrl] = useState('');
  const [viewChallanNumber, setViewChallanNumber] = useState('');
  const [viewLoading, setViewLoading] = useState(false);

  // Cleanup blob URL when modal closes
  useEffect(() => {
    if (!viewModalOpen && viewPdfUrl) {
      const url = viewPdfUrl;
      const t = setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setViewPdfUrl('');
      return () => clearTimeout(t);
    }
  }, [viewModalOpen, viewPdfUrl]);

  const applyFilters = () => {
    fetchDispatchHistory({
      po_number: filterPO,
      buyer: filterBuyer,
      date_from: filterDateFrom ? format(filterDateFrom, 'yyyy-MM-dd') : '',
      date_to: filterDateTo ? format(filterDateTo, 'yyyy-MM-dd') : ''
    });
  };

  const clearFilters = () => {
    setFilterPO('');
    setFilterBuyer('');
    setFilterDateFrom(null);
    setFilterDateTo(null);
    fetchDispatchHistory();
  };

  const downloadChallanPDF = async (challanNumber) => {
    toast.info('Generating PDF...');
    try {
      const result = await downloadFile(`/api/challan/pdf/${challanNumber}`, `${challanNumber}.pdf`, true);
      if (result.success) {
        toast.success('PDF downloaded!');
      } else {
        toast.error(result.error || 'Failed to download PDF');
      }
    } catch {
      toast.error('Download failed. Please try again.');
    }
  };

  // View opens the PDF inline in a modal with an iframe (no new tab → no pop-up blocker).
  const viewChallanPDF = async (challanNumber) => {
    setViewChallanNumber(challanNumber);
    setViewModalOpen(true);
    setViewLoading(true);
    setViewPdfUrl('');
    try {
      const { data } = await axios.get(`/api/challan/pdf/${challanNumber}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      setViewPdfUrl(url);
    } catch {
      toast.error('Failed to load challan PDF');
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200">
      {/* History Header */}
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History size={18} className="text-zinc-500" />
          <h2 className="font-heading font-bold text-lg text-[#09090B]">Previous Dispatch / Challan History</h2>
          {selectedPOData && (
            <span className="text-xs bg-zinc-100 px-2 py-1 rounded-sm text-zinc-600">
              Filtered by: {selectedPOData.po_number}
            </span>
          )}
          {!canEditDelete && (
            <span data-testid="view-only-badge" className="flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded-sm">
              <ShieldAlert size={12} />
              View Only – Editing Restricted
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="h-8 rounded-sm"
          data-testid="toggle-filters"
        >
          <Filter size={14} className="mr-1" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-4 bg-zinc-50 border-b border-zinc-200">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">PO Number</Label>
              <Input
                value={filterPO}
                onChange={(e) => setFilterPO(e.target.value)}
                placeholder="PO-..."
                className="mt-1 h-8 rounded-sm text-sm"
                data-testid="filter-po"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Buyer</Label>
              <Select value={filterBuyer || "all"} onValueChange={(v) => setFilterBuyer(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-8 rounded-sm text-sm">
                  <SelectValue placeholder="All Buyers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Buyers</SelectItem>
                  {buyers.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Date From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-1 h-8 rounded-sm text-sm justify-start">
                    <CalendarIcon size={12} className="mr-1" />
                    {filterDateFrom ? format(filterDateFrom, 'yyyy-MM-dd') : 'From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={filterDateFrom} onSelect={setFilterDateFrom} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Date To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-1 h-8 rounded-sm text-sm justify-start">
                    <CalendarIcon size={12} className="mr-1" />
                    {filterDateTo ? format(filterDateTo, 'yyyy-MM-dd') : 'To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={filterDateTo} onSelect={setFilterDateTo} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={applyFilters} className="h-8 rounded-sm bg-[#09090B]" data-testid="apply-filters">
              <Search size={12} className="mr-1" />
              Apply Filters
            </Button>
            <Button size="sm" variant="outline" onClick={clearFilters} className="h-8 rounded-sm" data-testid="clear-filters">
              <X size={12} className="mr-1" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* History Table */}
      <div className="max-h-[400px] overflow-y-auto">
        {historyLoading ? (
          <div className="p-8 text-center text-zinc-500">
            <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin mx-auto mb-2"></div>
            Loading history...
          </div>
        ) : dispatchHistory.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            No challan records found
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="dispatch-history-table">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">Challan #</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">Date</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">PO Number</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">Buyer</th>
                <th className="text-center p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">Items</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">Total Qty</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dispatchHistory.map((challan, idx) => {
                const itemsCount = challan.items ? challan.items.length : 1;
                const totalQty = challan.total_quantity || challan.quantity ||
                  (challan.items ? challan.items.reduce((sum, i) => sum + i.dispatch_qty, 0) : 0);

                return (
                  <tr
                    key={challan.challan_number}
                    className={`border-t border-zinc-200 hover:bg-zinc-50 ${idx === 0 ? 'bg-green-50' : ''}`}
                    data-testid={`history-row-${idx}`}
                  >
                    <td className="p-3 font-mono font-medium text-[#09090B]">
                      {challan.challan_number}
                      {idx === 0 && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Latest</span>}
                    </td>
                    <td className="p-3 font-mono text-zinc-600">{challan.dispatch_date}</td>
                    <td className="p-3 font-mono">{challan.po_number}</td>
                    <td className="p-3">{challan.buyer_name}</td>
                    <td className="p-3 text-center">
                      <span className="bg-zinc-100 px-2 py-0.5 rounded text-xs font-medium">
                        {itemsCount} SKU{itemsCount > 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-[#09090B]">{totalQty}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewChallanPDF(challan.challan_number)}
                          className="h-7 w-7 p-0 text-zinc-700 hover:text-[#09090B] hover:bg-zinc-100"
                          title="View PDF"
                          data-testid={`view-pdf-${challan.challan_number}`}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadChallanPDF(challan.challan_number)}
                          className="h-7 w-7 p-0"
                          title="Download PDF"
                          data-testid={`download-pdf-${challan.challan_number}`}
                        >
                          <Download size={14} />
                        </Button>
                        {canEditDelete && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(challan.challan_number)}
                              className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              title="Edit Challan"
                              data-testid={`edit-challan-${challan.challan_number}`}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteModal(challan)}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete Challan"
                              data-testid={`delete-challan-${challan.challan_number}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer with Pagination */}
      <div className="p-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
        <span className="text-xs text-zinc-500">
          Showing {dispatchHistory.length} of {historyTotal} challans
        </span>
        {historyTotalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={historyPage <= 1}
              onClick={() => { const p = historyPage - 1; setHistoryPage(p); fetchDispatchHistory({ page: p }); }}
              className="h-7 rounded-sm text-xs"
            >
              Previous
            </Button>
            <span className="text-xs text-zinc-500">Page {historyPage} of {historyTotalPages}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={historyPage >= historyTotalPages}
              onClick={() => { const p = historyPage + 1; setHistoryPage(p); fetchDispatchHistory({ page: p }); }}
              className="h-7 rounded-sm text-xs"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-5xl w-[90vw] h-[90vh] p-0 flex flex-col" data-testid="view-challan-pdf-dialog">
          <DialogHeader className="px-4 py-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 font-mono text-sm">
                <Eye size={16} className="text-zinc-600" />
                {viewChallanNumber}
              </span>
              {viewPdfUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-sm text-xs"
                  onClick={() => downloadChallanPDF(viewChallanNumber)}
                >
                  <Download size={12} className="mr-1" /> Download
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-zinc-100 overflow-hidden">
            {viewLoading && (
              <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
                <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin mr-2"></div>
                Loading PDF…
              </div>
            )}
            {!viewLoading && viewPdfUrl && (
              <iframe
                src={viewPdfUrl}
                title={viewChallanNumber}
                className="w-full h-full border-0"
                data-testid="view-challan-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
